import { api } from '../axios';
import {
  ReceiptVoucherUI,
  ReceiptVoucherResponse,
  CreateReceiptVoucherRequest,
  UpdateReceiptVoucherRequest,
  ReceiptVoucherListQuery,
  PaginatedReceiptVoucherResponse,
  ReceiptVoucherWithInvoicesResponse,
  ReceiptVoucherStatusCounts
} from '@/types/receiptVoucher';

export interface ApiResponse<T> {
  data: T;
  message: string;
  timestamp: string;
}

export const receiptVoucherApi = {
  // Create a new receipt voucher
  createReceiptVoucher: async (request: CreateReceiptVoucherRequest): Promise<ReceiptVoucherResponse> => {
    const response = await api.post<ApiResponse<ReceiptVoucherResponse>>('/receipt-vouchers', request);
    return response.data.data;
  },

  // Get all receipt vouchers with pagination and filtering
  getAllReceiptVouchers: async (query: ReceiptVoucherListQuery = {}): Promise<PaginatedReceiptVoucherResponse> => {
    const params = new URLSearchParams();
    
    // Add pagination parameters
    if (query.page) params.append('page', query.page.toString());
    if (query.page_size) params.append('page_size', query.page_size.toString());
    
    // Add search parameter
    if (query.search) params.append('search', query.search);
    
    // Add sorting parameters
    if (query.sort_by) params.append('sort_by', query.sort_by);
    if (query.sort_order) params.append('sort_order', query.sort_order);
    
    // Add filter parameters
    if (query.receipt_type) params.append('receipt_type', query.receipt_type);
    if (query.payment_method) params.append('payment_method', query.payment_method);
    if (query.payer_name) params.append('payer_name', query.payer_name);
    if (query.status) params.append('status', query.status);
    if (query.date_from) params.append('date_from', query.date_from);
    if (query.date_to) params.append('date_to', query.date_to);
    if (query.customer_id) params.append('customer_id', query.customer_id);

    const response = await api.get<PaginatedReceiptVoucherResponse>(`/receipt-vouchers?${params.toString()}`);
    return response.data;
  },

  // Get a specific receipt voucher by code
  getReceiptVoucher: async (code: string): Promise<ReceiptVoucherResponse> => {
    const response = await api.get<ApiResponse<ReceiptVoucherResponse>>(`/receipt-vouchers/${code}`);
    return response.data.data;
  },

  // Get receipt voucher with associated invoice details
  getReceiptVoucherWithInvoices: async (code: string): Promise<ReceiptVoucherWithInvoicesResponse> => {
    const response = await api.get<ApiResponse<ReceiptVoucherWithInvoicesResponse>>(`/receipt-vouchers/${code}/with-invoices`);
    return response.data.data;
  },

  // Update an existing receipt voucher
  updateReceiptVoucher: async (code: string, request: UpdateReceiptVoucherRequest): Promise<ReceiptVoucherResponse> => {
    const response = await api.put<ApiResponse<ReceiptVoucherResponse>>(`/receipt-vouchers/${code}`, request);
    return response.data.data;
  },

  // Delete a receipt voucher
  deleteReceiptVoucher: async (code: string): Promise<void> => {
    await api.delete(`/receipt-vouchers/${code}`);
  },

  // Complete a receipt voucher (change status to completed)
  completeReceiptVoucher: async (code: string): Promise<ReceiptVoucherResponse> => {
    const response = await api.post<ApiResponse<ReceiptVoucherResponse>>(`/receipt-vouchers/${code}/complete`);
    return response.data.data;
  },

  // Cancel a receipt voucher (change status to cancelled)
  cancelReceiptVoucher: async (code: string): Promise<ReceiptVoucherResponse> => {
    const response = await api.post<ApiResponse<ReceiptVoucherResponse>>(`/receipt-vouchers/${code}/cancel`);
    return response.data.data;
  },

  // Get status counts for filter tabs using optimized endpoint
  getStatusCounts: async (filters: Partial<ReceiptVoucherListQuery> = {}): Promise<ReceiptVoucherStatusCounts> => {
    try {
      const params = new URLSearchParams();

      // Add filter parameters (excluding status, page, page_size, sort_by, sort_order)
      if (filters.search) params.append('search', filters.search);
      if (filters.receipt_type) params.append('receipt_type', filters.receipt_type);
      if (filters.payment_method) params.append('payment_method', filters.payment_method);
      if (filters.payer_name) params.append('payer_name', filters.payer_name);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      if (filters.customer_id) params.append('customer_id', filters.customer_id);

      const response = await api.get<ApiResponse<ReceiptVoucherStatusCounts>>(`/receipt-vouchers/status-counts?${params.toString()}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching status counts:', error);
      return {
        all: 0,
        draft: 0,
        confirmed: 0,
        completed: 0,
        cancelled: 0
      };
    }
  },

  // Get customer collected debt total
  getCustomerCollectedDebt: async (customerCode: string): Promise<{ customer_code: string; total_collected: number }> => {
    const response = await api.get<ApiResponse<{ customer_code: string; total_collected: number }>>(`/receipt-vouchers/customer/${customerCode}/collected-debt`);
    return response.data.data;
  },
};

// Helper function to transform API response to UI format
export const transformReceiptVoucherToUI = (receiptVoucher: ReceiptVoucherResponse): ReceiptVoucherUI => {
  return {
    ...receiptVoucher,
    // Add any UI-specific transformations here
    invoice_count: receiptVoucher.invoice_ids.length,
    status_display: receiptVoucher.current_status.status.replace('_', ' ').toUpperCase(),
  };
};

// Helper function to format currency values
export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('vi-VN', {
    style: 'currency',
    currency: 'VND'
  }).format(amount);
};

// Helper function to format date values
export const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('vi-VN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
};
